import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../config';
import './AdminReports.css';

const AdminReports = () => {
  const [activeReport, setActiveReport] = useState('sales');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Sales Report State
  const [salesData, setSalesData] = useState([]);
  const [dateRange, setDateRange] = useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });
  
  // Top Products State
  const [topProducts, setTopProducts] = useState([]);
  const [productLimit, setProductLimit] = useState(10);
  
  // Dashboard Stats
  const [stats, setStats] = useState({
    total_products: 0,
    total_orders: 0,
    total_users: 0,
    total_revenue: 0,
    total_categories: 0
  });

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  useEffect(() => {
    if (activeReport === 'sales') {
      fetchSalesReport();
    } else if (activeReport === 'products') {
      fetchTopProducts();
    }
  }, [activeReport, dateRange, productLimit]);

  const fetchDashboardStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/dashboard`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Check if response is ok
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const text = await response.text();
      console.log('Dashboard stats response:', text);
      
      try {
        const data = JSON.parse(text);
        if (data.success) {
          setStats(data.data);
        } else {
          console.error('Dashboard stats error:', data.message);
        }
      } catch (parseError) {
        console.error('Failed to parse dashboard stats JSON:', parseError);
        setError('Invalid response from server');
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      setError('Error loading dashboard stats');
    }
  };

  const fetchSalesReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${API_BASE_URL}/admin/sales-report?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`;
      console.log('Fetching sales report from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const text = await response.text();
      console.log('Sales report response:', text);
      
      try {
        const data = JSON.parse(text);
        if (data.success) {
          setSalesData(data.data || []);
        } else {
          setError(data.message || 'Failed to fetch sales report');
        }
      } catch (parseError) {
        console.error('Failed to parse sales report JSON:', parseError);
        setError('Invalid response from server for sales report');
      }
    } catch (error) {
      console.error('Error fetching sales report:', error);
      setError('Error loading sales report');
    } finally {
      setLoading(false);
    }
  };

  const fetchTopProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${API_BASE_URL}/admin/top-products?limit=${productLimit}`;
      console.log('Fetching top products from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const text = await response.text();
      console.log('Top products response:', text);
      
      try {
        const data = JSON.parse(text);
        if (data.success) {
          setTopProducts(data.data || []);
        } else {
          setError(data.message || 'Failed to fetch top products');
        }
      } catch (parseError) {
        console.error('Failed to parse top products JSON:', parseError);
        setError('Invalid response from server for top products');
      }
    } catch (error) {
      console.error('Error fetching top products:', error);
      setError('Error loading top products');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '₹0.00';
    return `₹${parseFloat(price).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const calculateTotalSales = () => {
    return salesData.reduce((sum, day) => sum + (parseFloat(day.total_sales) || 0), 0);
  };

  const calculateTotalOrders = () => {
    return salesData.reduce((sum, day) => sum + (parseInt(day.orders_count) || 0), 0);
  };

  const calculateAverageOrderValue = () => {
    const totalOrders = calculateTotalOrders();
    const totalSales = calculateTotalSales();
    return totalOrders > 0 ? totalSales / totalOrders : 0;
  };

  const downloadReport = () => {
    if (activeReport === 'sales') {
      const csvData = salesData.map(day => ({
        Date: day.date,
        Orders: day.orders_count,
        'Total Sales': day.total_sales
      }));
      
      const csvContent = convertToCSV(csvData);
      downloadFile(csvContent, `sales_report_${dateRange.start_date}_to_${dateRange.end_date}.csv`);
    } else if (activeReport === 'products') {
      const csvData = topProducts.map(product => ({
        'Product Name': product.name,
        Brand: product.brand,
        Price: product.price,
        'Units Sold': product.total_quantity_sold || 0,
        'Total Revenue': product.total_revenue || 0,
        'Order Count': product.order_count || 0
      }));
      
      const csvContent = convertToCSV(csvData);
      downloadFile(csvContent, `top_products_report_${new Date().toISOString().split('T')[0]}.csv`);
    }
  };

  const convertToCSV = (data) => {
    if (data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const csvRows = [
      headers.join(','),
      ...data.map(row => headers.map(header => JSON.stringify(row[header] || '')).join(','))
    ];
    
    return csvRows.join('\n');
  };

  const downloadFile = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const printReport = () => {
    window.print();
  };

  if (error) {
    return (
      <div className="admin-reports-error">
        <p>Error: {error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="admin-reports">
      <div className="admin-reports-header">
        <h1>Reports & Analytics</h1>
      </div>

      {/* Quick Stats Cards */}
      <div className="stats-cards">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-value">{formatPrice(stats.total_revenue)}</div>
          <div className="stat-label">Total Revenue</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📦</div>
          <div className="stat-value">{stats.total_orders}</div>
          <div className="stat-label">Total Orders</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-value">{stats.total_users}</div>
          <div className="stat-label">Total Customers</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">👓</div>
          <div className="stat-value">{stats.total_products}</div>
          <div className="stat-label">Total Products</div>
        </div>
      </div>

      {/* Report Tabs */}
      <div className="report-tabs">
        <button 
          className={`tab ${activeReport === 'sales' ? 'active' : ''}`}
          onClick={() => setActiveReport('sales')}
        >
          📈 Sales Report
        </button>
        <button 
          className={`tab ${activeReport === 'products' ? 'active' : ''}`}
          onClick={() => setActiveReport('products')}
        >
          🏆 Top Products
        </button>
        <button 
          className={`tab ${activeReport === 'inventory' ? 'active' : ''}`}
          onClick={() => setActiveReport('inventory')}
        >
          📦 Inventory Report
        </button>
      </div>

      {/* Sales Report */}
      {activeReport === 'sales' && (
        <div className="report-content">
          <div className="report-controls">
            <div className="date-range">
              <div className="date-input">
                <label>From:</label>
                <input 
                  type="date" 
                  value={dateRange.start_date}
                  onChange={(e) => setDateRange({...dateRange, start_date: e.target.value})}
                />
              </div>
              <div className="date-input">
                <label>To:</label>
                <input 
                  type="date" 
                  value={dateRange.end_date}
                  onChange={(e) => setDateRange({...dateRange, end_date: e.target.value})}
                />
              </div>
            </div>
            <div className="report-actions">
              <button className="export-btn" onClick={downloadReport} disabled={salesData.length === 0}>
                📥 Export CSV
              </button>
              <button className="print-btn" onClick={printReport}>
                🖨️ Print
              </button>
            </div>
          </div>

          {/* Sales Summary Cards */}
          <div className="sales-summary">
            <div className="summary-card">
              <div className="summary-label">Total Sales</div>
              <div className="summary-value">{formatPrice(calculateTotalSales())}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Total Orders</div>
              <div className="summary-value">{calculateTotalOrders()}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Average Order Value</div>
              <div className="summary-value">{formatPrice(calculateAverageOrderValue())}</div>
            </div>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading sales data...</p>
            </div>
          ) : salesData.length === 0 ? (
            <div className="no-data">
              <div className="no-data-icon">📊</div>
              <h3>No Sales Data</h3>
              <p>No sales found for the selected date range.</p>
            </div>
          ) : (
            <div className="sales-table-container">
              <table className="sales-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Orders</th>
                    <th>Total Sales</th>
                    <th>Average Order</th>
                  </tr>
                </thead>
                <tbody>
                  {salesData.map((day, index) => (
                    <tr key={index}>
                      <td>{formatDate(day.date)}</td>
                      <td className="text-center">{day.orders_count}</td>
                      <td className="text-right">{formatPrice(day.total_sales)}</td>
                      <td className="text-right">
                        {day.orders_count > 0 
                          ? formatPrice(day.total_sales / day.orders_count)
                          : formatPrice(0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="total-row">
                    <td><strong>Total</strong></td>
                    <td className="text-center"><strong>{calculateTotalOrders()}</strong></td>
                    <td className="text-right"><strong>{formatPrice(calculateTotalSales())}</strong></td>
                    <td className="text-right"><strong>{formatPrice(calculateAverageOrderValue())}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Top Products Report */}
      {activeReport === 'products' && (
        <div className="report-content">
          <div className="report-controls">
            <div className="limit-selector">
              <label>Show top:</label>
              <select value={productLimit} onChange={(e) => setProductLimit(Number(e.target.value))}>
                <option value="5">5 Products</option>
                <option value="10">10 Products</option>
                <option value="20">20 Products</option>
                <option value="50">50 Products</option>
              </select>
            </div>
            <div className="report-actions">
              <button className="export-btn" onClick={downloadReport} disabled={topProducts.length === 0}>
                📥 Export CSV
              </button>
              <button className="print-btn" onClick={printReport}>
                🖨️ Print
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading top products...</p>
            </div>
          ) : topProducts.length === 0 ? (
            <div className="no-data">
              <div className="no-data-icon">🏆</div>
              <h3>No Product Data</h3>
              <p>No product sales data available.</p>
            </div>
          ) : (
            <div className="products-table-container">
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Product</th>
                    <th>Brand</th>
                    <th>Price</th>
                    <th>Units Sold</th>
                    <th>Revenue</th>
                    <th>Orders</th>
                  </tr>
                </thead>
                <tbody>
                  {topProducts.map((product, index) => (
                    <tr key={product.id}>
                      <td className="text-center rank-cell">
                        <span className={`rank-badge rank-${index + 1}`}>
                          {index + 1}
                        </span>
                      </td>
                      <td className="product-cell">
                        <div className="product-name">{product.name}</div>
                      </td>
                      <td>{product.brand || '-'}</td>
                      <td className="text-right">{formatPrice(product.price)}</td>
                      <td className="text-center">
                        <span className="units-sold">{product.total_quantity_sold || 0}</span>
                      </td>
                      <td className="text-right revenue-cell">
                        {formatPrice(product.total_revenue || 0)}
                      </td>
                      <td className="text-center">{product.order_count || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Inventory Report */}
      {activeReport === 'inventory' && (
        <div className="report-content">
          <div className="report-controls">
            <div className="report-actions">
              <button className="export-btn" onClick={() => alert('Coming soon!')}>
                📥 Export CSV
              </button>
              <button className="print-btn" onClick={printReport}>
                🖨️ Print
              </button>
            </div>
          </div>

          <div className="inventory-report">
            <div className="coming-soon">
              <div className="coming-soon-icon">🏗️</div>
              <h3>Inventory Report Coming Soon</h3>
              <p>We're working on detailed inventory analytics including:</p>
              <ul>
                <li>Stock level monitoring</li>
                <li>Low stock alerts</li>
                <li>Best selling variants</li>
                <li>Inventory turnover rates</li>
                <li>Seasonal demand patterns</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReports;